import Layers from "slotworks/graphics/layers/layers";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import FlexiText from "slotworks/graphics/elements/flexi-text";
import FlexiToggle, { ToggleEvent } from "slotworks/graphics/elements/flexi-toggle";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import Contract from "slotworks/utils/contract";
import soundManager from "slotworks/sound/sound-manager";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import { SoundIntroConfig } from "slotworks/config/component-config-schema";
import settingsModel from "slotworks/model/settings/settings-model";
import PromptContent from "slotworks/components/prompt/prompt-content";
import { Signal } from "signals";

export default class SoundIntroComponent {

    public onClose: Signal;

    private static DEFAULT_LAYER: string = "IntroPopups";
    private static DEFAULT_SCENE: string = "soundintro";

    private static BUTTON_OK: string = "btn-ok";
    private static TOGGLE_SOUND: string = "soundfx";

    private layerName: string;
    private scene: string;

    private layer: Layers;
    private okButton: FlexiButton;
    private okLabel: FlexiText;
    private soundFlexiToggle: FlexiToggle;
    private loadingLabel: FlexiText;
    private soundAnimation: AnimatedSprite;

    private inResolve: Function;
    
    private loaded: boolean = false;

    private enabled: boolean = false;
    private shown: boolean =  false;

    private savedSpaceToSpin:boolean;

    private gameIntro: PromptContent;

    constructor(config: SoundIntroConfig) {
        if(config) {
            this.enabled = config.enabled;
            this.layerName = config.layer || SoundIntroComponent.DEFAULT_LAYER;
            this.scene = config.scene || SoundIntroComponent.DEFAULT_SCENE;
        }
        this.onClose = new Signal();
    }

    public show(gameIntro: PromptContent = null): Contract<void> {
        if(this.enabled && !this.shown) {
            return new Contract(resolve => {
                this.shown = true;
                this.layer = Layers.FromString(this.layerName);
                if(!this.layer || !this.layer.hasScene(this.scene)) {
                    resolve(null);
                } else {        
                    this.layer.moveBehind(Layers.Header);
                    this.layer.setScene(this.scene).execute();
                    this.inResolve = resolve;
                    if(gameIntro) { // stopping active game intro pointer events
                        this.gameIntro = gameIntro;
                        gameIntro.disableContinueButton();
                        const toggle = Layers.Prompts.getToggle("show");
                        if(toggle) {
                            toggle.setEnabled(false);
                        }
                    } else {
                        Layers.BetBar.hide();
                        Layers.SmartButton.hide();
                        Layers.Header.hide();
                    }
                    // stopping space to spin until sound intro is showing                    
                    this.savedSpaceToSpin = settingsModel.getSpaceToSpin();
                    settingsModel.setSpaceToSpin(false);
                    this.setup();
                    this.layer.show();                    
                }
            });
        } else {
            return Contract.empty();
        }
    }

    public close = () => {
        if (this.soundFlexiToggle.getValue()) {
            soundManager.unmute();
        } else {
            soundManager.mute();
        }
        this.hide();
        settingsModel.setSpaceToSpin(this.savedSpaceToSpin);
        if(this.gameIntro) {
            this.gameIntro.enableContinueButton();
            const toggle = Layers.Prompts.getToggle("show");
            if(toggle) {
                toggle.setEnabled(true);
            }
            this.gameIntro = null;
        } else {
            Layers.BetBar.show();
            Layers.SmartButton.show();
            Layers.Header.show();
        }
        this.onClose.dispatch();
        this.inResolve(null);
    }

    private setSoundDisabled = (disabled: boolean) => {
        if (!this.loaded) {
            this.okButton.setVisible(disabled);
            this.okButton.setEnabled(disabled);
            if (this.okLabel) {
                this.okLabel.setVisible(disabled);
            }
            this.loadingLabel.setVisible(!disabled);
        } else {
            disabled ? soundManager.mute() : soundManager.unmute();
        }
    }

    private onSoundFlexiChange = () => {
        if (this.soundFlexiToggle.getValue()) {
            this.setSoundDisabled(false);
        } else {
            this.setSoundDisabled(true);
        }
    }

    private setup() {
        this.okButton = this.layer.getFlexiButton(SoundIntroComponent.BUTTON_OK);
        this.okButton.setEnabled(true);
        this.okButton.on(ButtonEvent.POINTER_UP, this.close);
        this.okLabel = this.layer.getFlexiText("btn-ok-lbl");
        this.loadingLabel = this.layer.getFlexiText("loading");
        this.soundAnimation = this.layer.getAnimatedSprite("audio_loading_icon");

        this.soundFlexiToggle = this.layer.getFlexiToggle(SoundIntroComponent.TOGGLE_SOUND);

        this.soundFlexiToggle.on(ToggleEvent.CHANGE, this.onSoundFlexiChange);

        const onDone = () => {
            this.loaded = true;
            this.okButton.setVisible(true);
            this.okButton.setEnabled(true);
            if (this.okLabel)
                this.okLabel.setVisible(true);
            this.loadingLabel.setVisible(false);
            if (this.soundAnimation)
                this.soundAnimation.gotoAndStop(0);
            if (this.soundFlexiToggle.getValue()) {
                soundManager.unmute();
            }
        }

        this.okButton.setVisible(false);
        this.okButton.setEnabled(false);
        if (this.okLabel)
            this.okLabel.setVisible(false);

        if (soundManager.getLoaded()) {
            onDone();
        } else if (soundManager.getLoading()) {
            soundManager.onLoadComplete.addOnce(onDone);
        } else {
            soundManager.loadSounds();
            soundManager.onLoadComplete.addOnce(onDone);
        }

        if (soundManager.getMuted()) {
            if (this.soundFlexiToggle) {
                this.soundFlexiToggle.setValue(false);
            }
        } else {
            if (this.soundFlexiToggle) {
                this.soundFlexiToggle.setValue(true);
            }
        }
    }

    private hide() {
        this.okButton.off(ButtonEvent.POINTER_UP, this.close);
        this.okButton.setEnabled(false);
        this.soundFlexiToggle.off(ToggleEvent.CHANGE, this.onSoundFlexiChange);
        this.layer.defaultScene().execute();
        this.layer.hide();
    }
}


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/sound/sound-intro-component.ts